import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import {
  COLORS,
  CUBE_BODY_COLOR,
  EMPTY_STICKER_COLOR,
  FACE_CONFIG,
  FACE_ORDER,
  STICKER_MATERIAL,
} from '../core/cube-config.js';
import { GRID_COORDINATES_4X4, STICKERS_PER_FACE_4X4, stickerPosition4x4 } from '../core/cube-config-4x4.js';
import { getMoveAnimationSpec4x4, stickerBelongsToMoveLayer4x4 } from '../core/moves-4x4.js';
import {
  cameraContainsBox,
  fitCameraToBox,
} from './camera-fit.js';
import {
  createViewDiagnostics,
  monitorWebGLContext,
  RafResizeCoordinator,
  shouldResizeRenderer,
} from './view-resize.js';

const STICKER_SIZE = 0.76;
const DEFAULT_CAMERA_POSITION = new THREE.Vector3(7.4, 6.2, 8.4);
const BASE_MAX_DISTANCE = 15;

function createRoundedStickerGeometry(size, radius) {
  const half = size / 2;
  const shape = new THREE.Shape();
  shape.moveTo(-half + radius, -half);
  shape.lineTo(half - radius, -half);
  shape.quadraticCurveTo(half, -half, half, -half + radius);
  shape.lineTo(half, half - radius);
  shape.quadraticCurveTo(half, half, half - radius, half);
  shape.lineTo(-half + radius, half);
  shape.quadraticCurveTo(-half, half, -half, half - radius);
  shape.lineTo(-half, -half + radius);
  shape.quadraticCurveTo(-half, -half, -half + radius, -half);
  return new THREE.ShapeGeometry(shape);
}

function orientTowardNormal(object, normal) {
  object.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), new THREE.Vector3(...normal));
}

export class CubeView4x4 {
  constructor(container, { interactive = false, onStickerSelect = (_selection) => {} } = {}) {
    this.container = container;
    this.interactive = interactive;
    this.onStickerSelect = onStickerSelect;
    this.stickerMeshes = new Map();
    this.clickableMeshes = [];
    this.cubies = [];
    this.pointerStart = null;
    this.animationFrame = null;
    this.isAnimating = false;
    this.disposed = false;

    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(34, 1, 0.1, 100);
    this.camera.position.copy(DEFAULT_CAMERA_POSITION);
    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: 'high-performance' });
    const constrainedDevice = navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 4;
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, constrainedDevice ? 1.35 : 1.75));
    this.renderer.setClearColor(0x000000, 0);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1;
    this.renderer.domElement.setAttribute('aria-label', interactive
      ? '可旋转并点击 96 张贴纸录入颜色的 4×4 3D 魔方'
      : '可旋转观察并播放还原动作的 4×4 3D 魔方');
    this.renderer.domElement.setAttribute('role', 'img');
    this.renderer.domElement.style.touchAction = 'none';
    container.append(this.renderer.domElement);
    this.diagnostics = createViewDiagnostics('CubeView 4×4');
    this.webglContext = monitorWebGLContext(this.renderer.domElement, this.diagnostics);

    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.07;
    this.controls.enablePan = false;
    this.controls.minDistance = 6.8;
    this.controls.maxDistance = BASE_MAX_DISTANCE;
    this.controls.rotateSpeed = 0.68;
    this.controls.zoomSpeed = 0.78;
    this.controls.touches = { ONE: THREE.TOUCH.ROTATE, TWO: THREE.TOUCH.DOLLY_ROTATE };

    this.#createLighting();
    this.#createCubeBody();
    this.#createStickers();
    this.#createFloorGlow();
    this.modelBounds = new THREE.Box3().setFromObject(this.cubeGroup);
    for (const sticker of this.stickerMeshes.values()) this.modelBounds.expandByObject(sticker);
    this.hasInitialCameraFit = false;
    this.lastObservedSize = null;
    this.rendererSize = null;
    this.raycaster = new THREE.Raycaster();
    this.pointer = new THREE.Vector2();
    this.handlePointerDown = (event) => { this.pointerStart = { x: event.clientX, y: event.clientY }; };
    this.handlePointerCancel = () => { this.pointerStart = null; };
    this.handlePointerUp = (event) => this.#handlePointerUp(event);
    this.renderer.domElement.addEventListener('pointerdown', this.handlePointerDown);
    this.renderer.domElement.addEventListener('pointerup', this.handlePointerUp);
    this.renderer.domElement.addEventListener('pointercancel', this.handlePointerCancel);

    this.resizeCoordinator = new RafResizeCoordinator((source) => this.resize(source));
    this.resizeObserver = new ResizeObserver(() => {
      this.resizeCoordinator.schedule('resize-observer');
    });
    this.resizeObserver.observe(container);
    this.resize('initial');
    this.#animate();
  }

  #createLighting() {
    this.scene.add(new THREE.AmbientLight(0xffffff, 0.76));
    this.scene.add(new THREE.HemisphereLight(0xf1f4ff, 0x30394b, 1.85));
    const key = new THREE.DirectionalLight(0xffffff, 2.1);
    key.position.set(6, 9, 8);
    this.scene.add(key);
    const fill = new THREE.DirectionalLight(0xc8d8ff, 1.2);
    fill.position.set(-7, -1, 6);
    this.scene.add(fill);
    const rim = new THREE.DirectionalLight(0x8ca5ff, 1.2);
    rim.position.set(-8, 3, -6);
    this.scene.add(rim);
  }

  #createCubeBody() {
    const geometry = new THREE.BoxGeometry(0.94, 0.94, 0.94);
    const material = new THREE.MeshStandardMaterial({ color: CUBE_BODY_COLOR, roughness: 0.46, metalness: 0.06 });
    this.cubeGroup = new THREE.Group();
    for (const logicalX of GRID_COORDINATES_4X4) {
      for (const logicalY of GRID_COORDINATES_4X4) {
        for (const logicalZ of GRID_COORDINATES_4X4) {
          const cubie = new THREE.Mesh(geometry, material);
          cubie.position.set(logicalX / 2, logicalY / 2, logicalZ / 2);
          cubie.userData.homePosition = [logicalX / 2, logicalY / 2, logicalZ / 2];
          cubie.userData.logicalPosition = [logicalX, logicalY, logicalZ];
          this.cubeGroup.add(cubie);
          this.cubies.push(cubie);
        }
      }
    }
    this.scene.add(this.cubeGroup);
  }

  #createStickers() {
    const geometry = createRoundedStickerGeometry(STICKER_SIZE, 0.075);
    for (const face of FACE_ORDER) {
      for (let index = 0; index < STICKERS_PER_FACE_4X4; index += 1) {
        const material = new THREE.MeshStandardMaterial({
          color: EMPTY_STICKER_COLOR,
          emissive: EMPTY_STICKER_COLOR,
          emissiveIntensity: STICKER_MATERIAL.emissiveEmpty,
          roughness: STICKER_MATERIAL.roughnessEmpty,
          metalness: STICKER_MATERIAL.metalness,
          side: THREE.FrontSide,
        });
        const sticker = new THREE.Mesh(geometry, material);
        const position = stickerPosition4x4(face, index);
        sticker.position.set(position[0], position[1], position[2]);
        orientTowardNormal(sticker, FACE_CONFIG[face].normal);
        sticker.userData = { face, index };
        sticker.renderOrder = 2;
        this.scene.add(sticker);
        this.stickerMeshes.set(`${face}${index}`, sticker);
        this.clickableMeshes.push(sticker);
      }
    }
  }

  #createFloorGlow() {
    const glow = new THREE.Mesh(
      new THREE.CircleGeometry(4.5, 64),
      new THREE.MeshBasicMaterial({ color: 0x3159b8, transparent: true, opacity: 0.1, depthWrite: false }),
    );
    glow.rotation.x = -Math.PI / 2;
    glow.position.y = -2.75;
    this.scene.add(glow);
  }

  #handlePointerUp(event) {
    if (!this.interactive || !this.pointerStart) return;
    const distance = Math.hypot(event.clientX - this.pointerStart.x, event.clientY - this.pointerStart.y);
    this.pointerStart = null;
    if (distance > 9) return;
    const rect = this.renderer.domElement.getBoundingClientRect();
    this.pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    this.raycaster.setFromCamera(this.pointer, this.camera);
    const [hit] = this.raycaster.intersectObjects(this.clickableMeshes, false);
    if (hit) this.onStickerSelect(hit.object.userData);
  }

  setStickers(stickers) {
    for (const face of FACE_ORDER) {
      for (let index = 0; index < STICKERS_PER_FACE_4X4; index += 1) {
        const color = stickers[face][index];
        const mesh = this.stickerMeshes.get(`${face}${index}`);
        const displayColor = color ? COLORS[color].hex : EMPTY_STICKER_COLOR;
        mesh.material.color.set(displayColor);
        mesh.material.emissive.set(displayColor);
        mesh.material.emissiveIntensity = color ? STICKER_MATERIAL.emissiveFilled : STICKER_MATERIAL.emissiveEmpty;
        mesh.material.roughness = color ? STICKER_MATERIAL.roughnessFilled : STICKER_MATERIAL.roughnessEmpty;
      }
    }
  }

  #resetStickerTransform(sticker) {
    const { face, index } = sticker.userData;
    const position = stickerPosition4x4(face, index);
    sticker.position.set(position[0], position[1], position[2]);
    sticker.quaternion.identity();
    orientTowardNormal(sticker, FACE_CONFIG[face].normal);
  }

  async animateMove(move, { duration = 460, finalStickers = null } = {}) {
    if (this.isAnimating) throw new Error('上一动作动画尚未结束');
    if (this.disposed) throw new Error('4×4 3D 视图已经关闭');
    this.isAnimating = true;
    const spec = getMoveAnimationSpec4x4(move);
    const axis = new THREE.Vector3(...spec.axis);
    const turnGroup = new THREE.Group();
    this.scene.add(turnGroup);
    const turningCubies = this.cubies.filter((cubie) => spec.layerCoordinates.includes(cubie.userData.logicalPosition[spec.axisIndex]));
    const turningStickers = [...this.stickerMeshes.values()].filter((sticker) => (
      stickerBelongsToMoveLayer4x4(sticker.userData.face, sticker.userData.index, spec)
    ));
    turningCubies.forEach((cubie) => turnGroup.attach(cubie));
    turningStickers.forEach((sticker) => turnGroup.attach(sticker));
    const startedAt = performance.now();
    const ease = (progress) => 1 - (1 - progress) ** 3;
    try {
      await new Promise((resolve, reject) => {
        const frame = (time) => {
          if (this.disposed) return reject(new Error('4×4 3D 视图已经关闭'));
          const progress = Math.min((time - startedAt) / duration, 1);
          turnGroup.quaternion.setFromAxisAngle(axis, spec.angleRadians * ease(progress));
          if (progress < 1) requestAnimationFrame(frame);
          else resolve();
        };
        requestAnimationFrame(frame);
      });
    } finally {
      turningCubies.forEach((cubie) => {
        this.cubeGroup.attach(cubie);
        const [x, y, z] = cubie.userData.homePosition;
        cubie.position.set(x, y, z);
        cubie.quaternion.identity();
      });
      turningStickers.forEach((sticker) => {
        this.scene.attach(sticker);
        this.#resetStickerTransform(sticker);
      });
      this.scene.remove(turnGroup);
      if (finalStickers) this.setStickers(finalStickers);
      this.isAnimating = false;
    }
  }

  resize(source = 'manual') {
    const width = Math.max(this.container.clientWidth, 1);
    const height = Math.max(this.container.clientHeight, 1);
    const nextViewportSize = { width, height };
    const previousObservedSize = this.lastObservedSize;
    const resizeRenderer = shouldResizeRenderer(this.rendererSize, nextViewportSize);
    let fittedCamera = false;
    if (!resizeRenderer) {
      this.lastObservedSize = nextViewportSize;
      this.diagnostics.record('resize', {
        source,
        oldSize: previousObservedSize,
        newSize: nextViewportSize,
        rendererResized: false,
        fittedCamera: false,
      });
      return;
    }
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height, false);
    this.rendererSize = nextViewportSize;
    this.diagnostics.record('renderer-set-size', { source, size: nextViewportSize });
    if (width >= 32 && height >= 32) {
      const needsInitialFit = !this.hasInitialCameraFit;
      const becameClipped = !needsInitialFit && !cameraContainsBox(this.camera, this.modelBounds);
      if (needsInitialFit || becameClipped) {
        const distance = fitCameraToBox({
          camera: this.camera,
          controls: this.controls,
          box: this.modelBounds,
          defaultPosition: DEFAULT_CAMERA_POSITION,
          baseMaxDistance: BASE_MAX_DISTANCE,
          preserveDirection: this.hasInitialCameraFit,
        });
        this.hasInitialCameraFit = true;
        fittedCamera = true;
        this.diagnostics.record('fit-camera', {
          source,
          reason: needsInitialFit ? 'initial' : 'model-clipped',
          distance,
          size: nextViewportSize,
        });
      }
    }
    this.lastObservedSize = nextViewportSize;
    this.diagnostics.record('resize', {
      source,
      oldSize: previousObservedSize,
      newSize: nextViewportSize,
      rendererResized: true,
      fittedCamera,
    });
  }

  #animate() {
    this.controls.update();
    if (!this.webglContext.lost) this.renderer.render(this.scene, this.camera);
    this.animationFrame = requestAnimationFrame(() => this.#animate());
  }

  dispose() {
    this.disposed = true;
    cancelAnimationFrame(this.animationFrame);
    this.resizeCoordinator.dispose();
    this.resizeObserver.disconnect();
    this.webglContext.dispose();
    this.renderer.domElement.removeEventListener('pointerdown', this.handlePointerDown);
    this.renderer.domElement.removeEventListener('pointerup', this.handlePointerUp);
    this.renderer.domElement.removeEventListener('pointercancel', this.handlePointerCancel);
    this.controls.dispose();
    this.scene.traverse((object) => {
      const mesh = /** @type {THREE.Mesh} */ (object);
      mesh.geometry?.dispose();
      if (Array.isArray(mesh.material)) mesh.material.forEach((material) => material.dispose());
      else mesh.material?.dispose();
    });
    this.renderer.dispose();
    this.renderer.domElement.remove();
  }
}
